import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet";
import { FaLocationDot, FaRegClock, FaBriefcase } from "react-icons/fa6";
import Header from "../components/header";
import Footer from "../components/footer";
import { useCareersContent } from "../hooks/careers-content";
import { WEB_ROUTES } from "../routes/web-routes";

export default function Careers() {
  const { content, page, isLoading } = useCareersContent();

  const jobs = content?.jobs ?? [];

  return (
    <>
      <Helmet>
        <title>{`Careers - Nant Power`}</title>
        <meta
          name="description"
          content={`Explore current job openings at Nant Power and join a team delivering reliable, large-scale power generation in Sierra Leone.`}
        />
      </Helmet>

      <Header />
      <main>
        {/* Hero */}
        <section className="bg-white md:py-16 py-8">
          <div className="container mx-auto md:px-0 px-8">
            <div className="text-base leading-7 ">
              <div className=" flex justify-between items-center gap-x-4 mb-2">
                <h1 className="text-4xl font-medium ">
                  {page?.title?.rendered}
                </h1>
              </div>
              <div
                className="text-[#58b947] text-2xl font-medium"
                dangerouslySetInnerHTML={{
                  __html: page?.content?.rendered,
                }}
              />
            </div>
          </div>
        </section>

        {/* Current openings */}
        <section className="bg-gray-100 md:py-16 py-8">
          <div className="container mx-auto md:px-0 px-8">
            <div className="mb-10">
              <h2 className="text-3xl font-medium mb-2 text-[#58b947]">
                {"Current openings"}
              </h2>
            </div>

            {isLoading ? (
              <p className="text-lg text-gray-600">Loading current openings…</p>
            ) : jobs.length === 0 ? (
              <p className="text-lg text-gray-600">
                There are no open positions at the moment. Please check back
                soon.
              </p>
            ) : (
              <div className="space-y-6">
                {jobs.map((job, index) => {
                  const jobRef =
                    job.slug ?? (job.id ? String(job.id) : String(index + 1));

                  return (
                    <div
                      key={job.id ? String(job.id) : `${job.title}-${index}`}
                      className="bg-white border-2 border-gray-200 hover:border-[#58b947] transition-all duration-300 p-8 rounded-lg"
                    >
                      <div className="flex flex-wrap gap-2 mb-3">
                        {job.department && (
                          <span className="inline-block px-4 py-1 bg-[#58b947]/10 text-[#58b947] text-sm font-medium">
                            {job.department}
                          </span>
                        )}
                        {job.grade && (
                          <span className="inline-block px-4 py-1 bg-[#58b947]/10 text-[#58b947] text-sm font-medium">
                            Grade: {job.grade}
                          </span>
                        )}
                      </div>

                      <h3 className="text-2xl font-medium mb-4">
                        <NavLink to={`${WEB_ROUTES.CAREERS}/${jobRef}`}>
                          {job.title}
                        </NavLink>
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        {job.description}
                      </p>

                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        {job.location && (
                          <div className="flex items-center gap-2">
                            <FaLocationDot className="w-4 h-4 text-[#58b947]" />
                            <span>{job.location}</span>
                          </div>
                        )}
                        {job.type && (
                          <div className="flex items-center gap-2">
                            <FaBriefcase className="w-4 h-4 text-[#58b947]" />
                            <span>{job.type}</span>
                          </div>
                        )}
                        {job.closingDate && (
                          <div className="flex items-center gap-2">
                            <FaRegClock className="w-4 h-4 text-[#58b947]" />
                            <span>Closing: {job.closingDate}</span>
                          </div>
                        )}
                      </div>

                      <NavLink
                        to={`${WEB_ROUTES.CAREERS}/${jobRef}`}
                        className="bg-[#58b947] px-5 py-2 text-white inline-block whitespace-nowrap mt-6"
                      >
                        View job
                        <i className="fi fi-rr-arrow-right text-xl float-right mt-0.5 ml-1.5"></i>
                      </NavLink>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
